import { useMemo } from 'react';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import ValueEditor from '../ValueEditors';
import SelectionDialogEditor from '../ValueEditors/SelectionDialogEditor';
import RemoteSearchEditor from '../ValueEditors/RemoteSearchEditor';
import StoreListImport from '../StoreListImport';
import FieldLabel from './FieldLabel';
import {
    COUNTER_FIELD_NAME,
    DIALOG_SELECT_FIELD_NAMES,
    REMOTE_SEARCH_FIELD_CRITERIA,
    ROW_SPAN_2_FIELD_NAMES,
    STORE_FIELD_NAME,
    resolveFieldLabel,
} from './fieldConfig';
import { resolveFieldValues } from './fieldValues';
import { FieldRowProps, ruleIdOf } from './rowProps';

function PairedValue({ field, rule, onChange }: { field: QueryField; rule: QueryRule | undefined; onChange: (value: string) => void }) {
    const values = field.values ?? [];
    if (values.length === 1) {
        return <Typography variant="body2" sx={{ color: 'text.primary' }}>{values[0].label}</Typography>;
    }
    if (values.length === 0) return null;
    return (
        <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
            <Select value={typeof rule?.value === 'string' ? rule.value : ''} onChange={(e) => onChange(e.target.value)}>
                {values.map(v => (
                    <MenuItem key={v.name} value={v.name}>{v.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function DefaultRow({ field, rule, pairedField, pairedRule, onUpdateField }: FieldRowProps) {
    const { t } = useLanguage();
    const values = useMemo(() => resolveFieldValues(field, t), [field, t]);
    const resolvedField = useMemo(() => ({ ...field, values }), [field, values]);
    const label = resolveFieldLabel(field, t);
    const fillHeight = ROW_SPAN_2_FIELD_NAMES.has(field.name);
    const handleValueChange = (value: QueryRule['value']) => onUpdateField(field.name, { value });

    const renderEditor = () => {
        if (DIALOG_SELECT_FIELD_NAMES.has(field.name)) {
            // Sélection multiple : certains champs (ex. ville) ont « = » comme opérateur par défaut, ce qui ferait comparer « a,b » tel quel
            return <SelectionDialogEditor value={rule?.value} values={values} title={label} onChange={(value) => onUpdateField(field.name, { operator: 'in', value })} />;
        }
        const criteria = REMOTE_SEARCH_FIELD_CRITERIA[field.name];
        if (criteria) {
            return <RemoteSearchEditor criteria={criteria} value={rule?.value} onChange={handleValueChange} placeholder={label} />;
        }
        return (
            <ValueEditor
                ruleId={ruleIdOf(field, rule)}
                field={resolvedField}
                operator={rule?.operator || field.defaultOperator || '='}
                value={rule?.value}
                onChange={handleValueChange}
                compact
                fillHeight={fillHeight}
                betweenSwapIfInverted={field.name === COUNTER_FIELD_NAME}
            />
        );
    };

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: fillHeight ? 'stretch' : 'center', gap: 2, flexGrow: 1 }}>
                <Box sx={pairedField ? { flex: '0 1 260px', minWidth: '220px' } : { flexGrow: 1, minWidth: '200px', alignSelf: 'stretch' }}>
                    {renderEditor()}
                </Box>
                {pairedField && (
                    <PairedValue field={pairedField} rule={pairedRule} onChange={(value) => onUpdateField(pairedField.name, { value })} />
                )}
                {field.name === STORE_FIELD_NAME && <StoreListImport values={field.values ?? []} onChange={handleValueChange} />}
            </Box>
        </>
    );
}

export default DefaultRow;
